import React, { ErrorInfo } from "react";
import { Icon } from "../ui";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  info: ErrorInfo | null;
  detailsOpen: boolean;
}

export default class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      info: null,
      detailsOpen: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ error, info });
    console.error("ErrorBoundary caught an error:", error, info);
    if (typeof this.props.onError === "function") {
      try {
        this.props.onError(error, info);
      } catch (e) {
        console.error("ErrorBoundary onError handler threw:", e);
      }
    }
  }

  handleReload = () => {
    this.setState(
      { hasError: false, error: null, info: null, detailsOpen: false },
      () => {
        setTimeout(() => {
          window.location.reload();
        }, 50);
      },
    );
  };

  toggleDetails = () => {
    this.setState((s) => ({ detailsOpen: !s.detailsOpen }));
  };

  renderFallback() {
    const { fallback } = this.props;
    const { error, info, detailsOpen } = this.state;

    if (fallback) return fallback;

    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          padding: 28,
          background: "var(--bg)",
        }}
      >
        <div className="card" style={{ maxWidth: 560, width: "100%" }}>
          <div className="empty">
            <div
              style={{
                color: "var(--danger)",
                display: "grid",
                placeItems: "center",
                width: 44,
                height: 44,
                background: "var(--danger-soft)",
                borderRadius: 999,
              }}
            >
              <Icon name="x" size={20} />
            </div>
            <h3>Something went wrong</h3>
            <p>
              The console hit an unexpected error. Try reloading the page. If
              the problem persists, share the details below with the operations
              team.
            </p>
            <p
              style={{
                fontFamily: "IBM Plex Mono, monospace",
                fontSize: 11,
                color: "var(--muted)",
              }}
            >
              {error ? `${error.name}: ${error.message}` : "Unknown error"}
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn primary" onClick={this.handleReload}>
                <Icon name="refresh" size={14} /> Reload
              </button>
              <button className="btn ghost" onClick={this.toggleDetails}>
                {detailsOpen ? "Hide details" : "Show details"}
              </button>
            </div>
            {detailsOpen && (
              <pre
                style={{
                  marginTop: 12,
                  padding: 12,
                  background: "var(--surface-alt)",
                  border: "1px solid var(--line)",
                  borderRadius: 8,
                  fontFamily: "IBM Plex Mono, monospace",
                  fontSize: 11,
                  color: "var(--ink-2)",
                  textAlign: "left",
                  maxHeight: 240,
                  overflow: "auto",
                  whiteSpace: "pre-wrap",
                }}
              >
                {info?.componentStack ?? error?.stack ?? "No stack available"}
              </pre>
            )}
          </div>
        </div>
      </div>
    );
  }

  render() {
    if (this.state.hasError) {
      try {
        return this.renderFallback();
      } catch {
        return (
          <div style={{ padding: 20 }}>
            <h2>Critical error</h2>
            <p>Something went wrong and the fallback UI failed to render.</p>
            <button onClick={() => window.location.reload()}>Reload</button>
          </div>
        );
      }
    }
    return this.props.children as React.ReactElement;
  }
}

export const withErrorBoundary =
  (Component: React.ComponentType<any>, fallback?: React.ReactNode) =>
  (props: any) => (
    <ErrorBoundary fallback={fallback}>
      <Component {...props} />
    </ErrorBoundary>
  );
